import React, { useEffect, useMemo, useState } from 'react';
import { X, Filter, GitBranch, ShieldAlert, ArrowRight, Settings2, Plus, Trash2, Check, Sparkles } from 'lucide-react';
import { DialingList } from './DialingListGrid';
interface CriteriaPanelProps {
  list: DialingList | null;
  onClose: () => void;
}
type Tone = 'default' | 'success';
type GroupKey = 'matchAll' | 'matchAny' | 'exclusions';
interface Rule {
  id: string;
  label: string;
  sub?: string;
  op: string;
  value: string;
  tone?: Tone;
}
const OPERATORS = ['>', '>=', '<', '<=', '==', '!=', 'IN', 'NOT IN'] as const;
const DEFAULT_RULES: Record<GroupKey, Rule[]> = {
  matchAll: [{
    id: 'r1',
    label: 'FICO Score',
    sub: 'Credit Profile',
    op: '>=',
    value: '620'
  }, {
    id: 'r2',
    label: 'Loan Amount',
    sub: 'Property Details',
    op: '>',
    value: '$250,000'
  }, {
    id: 'r3',
    label: 'Credit Run',
    sub: 'Status',
    op: '==',
    value: 'True',
    tone: 'success'
  }],
  matchAny: [{
    id: 'r4',
    label: 'Loan Type',
    op: 'IN',
    value: 'VA Cash Out'
  }, {
    id: 'r5',
    label: 'Rate',
    op: '>',
    value: '6.5%'
  }],
  exclusions: [{
    id: 'r6',
    label: 'DNC Registry',
    sub: 'Global Compliance',
    op: '==',
    value: 'Active'
  }]
};
const FIELD_SUGGESTIONS = ['FICO Score', 'Loan Amount', 'Rate', 'Loan Type', 'State', 'Occupancy', 'Lead Source', 'Campaign'];
function uid() {
  return 'r_' + Math.random().toString(36).slice(2, 9);
}
export function CriteriaPanel({
  list,
  onClose
}: CriteriaPanelProps) {
  const displayName = list?.name ?? '';
  const [isEditing, setIsEditing] = useState(false);
  const [rules, setRules] = useState<Record<GroupKey, Rule[]>>(DEFAULT_RULES);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<{
    projected: number;
    delta: number;
  } | null>(null);
  useEffect(() => {
    if (list) {
      setIsEditing(false);
      setRules(DEFAULT_RULES);
      setSavedAt(null);
      setSimulation(null);
    }
  }, [list?.id]);
  const totalRules = useMemo(() => rules.matchAll.length + rules.matchAny.length + rules.exclusions.length, [rules]);
  const updateRule = (group: GroupKey, id: string, patch: Partial<Rule>) => {
    setRules(prev => ({
      ...prev,
      [group]: prev[group].map(r => r.id === id ? {
        ...r,
        ...patch
      } : r)
    }));
  };
  const deleteRule = (group: GroupKey, id: string) => {
    setRules(prev => ({
      ...prev,
      [group]: prev[group].filter(r => r.id !== id)
    }));
  };
  const addRule = (group: GroupKey) => {
    setRules(prev => ({
      ...prev,
      [group]: [...prev[group], {
        id: uid(),
        label: 'New Field',
        op: '==',
        value: ''
      }]
    }));
  };
  const handleSave = () => {
    setIsEditing(false);
    const now = new Date();
    setSavedAt(now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    }));
  };
  const handleCancel = () => {
    setIsEditing(false);
    setRules(DEFAULT_RULES);
  };
  const handleSimulate = () => {
    // Deterministic projection from list size + number of rules
    const base = list?.leadCount ?? 10000;
    const ruleImpact = totalRules * 0.04;
    const projected = Math.max(100, Math.floor(base * (1 - ruleImpact)));
    setSimulation({
      projected,
      delta: projected - (list?.leadCount ?? 0)
    });
  };
  return <div className="h-full w-full md:w-[480px] bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col" data-id="element-69">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/95 backdrop-blur" data-id="element-70">
        <div className="min-w-0" data-id="element-71">
          <div className="flex items-center gap-2 mb-1" data-id="element-72">
            <GitBranch className="w-4 h-4 text-indigo-400" data-id="element-73" />
            <h2 className="text-lg font-semibold text-white" data-id="element-74">
              {isEditing ? 'Edit Criteria' : 'Live Criteria'}
            </h2>
            {isEditing && <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-[10px] font-bold rounded border border-amber-500/20 uppercase tracking-wider" data-id="element-75">
                Editing
              </span>}
          </div>
          <p className="text-sm text-slate-400 truncate" data-id="element-76">
            Routing logic for{' '}
            <span className="text-slate-200 font-medium" data-id="element-77">{displayName}</span>
            {savedAt && !isEditing && <span className="ml-2 text-emerald-400 text-xs" data-id="element-78">
                · Saved at {savedAt}
              </span>}
          </p>
        </div>
        <button onClick={onClose} aria-label="Close criteria panel" className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors shrink-0" data-id="element-79">
          <X className="w-5 h-5" data-id="element-80" />
        </button>
      </div>

      {/* Simulation Banner */}
      {simulation && !isEditing && <div className="px-6 py-3 bg-indigo-500/5 border-b border-indigo-500/20 flex items-center justify-between gap-3" data-id="element-81">
          <div className="flex items-center gap-3 min-w-0" data-id="element-82">
            <div className="p-1.5 bg-indigo-500/10 rounded" data-id="element-83">
              <Sparkles className="w-3.5 h-3.5 text-indigo-400" data-id="element-84" />
            </div>
            <div className="min-w-0" data-id="element-85">
              <div className="text-xs text-slate-400" data-id="element-86">Simulated population</div>
              <div className="text-sm font-bold text-white tabular-nums" data-id="element-87">
                {simulation.projected.toLocaleString()} leads
                <span className={`ml-2 text-xs font-medium ${simulation.delta >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-id="element-88">
                  {simulation.delta >= 0 ? '+' : ''}
                  {simulation.delta.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => setSimulation(null)} aria-label="Dismiss simulation" className="p-1 text-slate-500 hover:text-slate-300 rounded shrink-0" data-id="element-89">
            <X className="w-4 h-4" data-id="element-90" />
          </button>
        </div>}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-91">
        <RuleGroup label="MATCH ALL" tone="indigo" isEditing={isEditing} rules={rules.matchAll} onAdd={() => addRule('matchAll')} onUpdate={(id, patch) => updateRule('matchAll', id, patch)} onDelete={id => deleteRule('matchAll', id)} data-id="element-92" />

        <RuleGroup label="MATCH ANY (OR)" tone="amber" isEditing={isEditing} rules={rules.matchAny} connected onAdd={() => addRule('matchAny')} onUpdate={(id, patch) => updateRule('matchAny', id, patch)} onDelete={id => deleteRule('matchAny', id)} data-id="element-93" />

        <RuleGroup label="EXCLUSIONS" tone="rose" isEditing={isEditing} rules={rules.exclusions} isExclusion onAdd={() => addRule('exclusions')} onUpdate={(id, patch) => updateRule('exclusions', id, patch)} onDelete={id => deleteRule('exclusions', id)} data-id="element-94" />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/95 backdrop-blur flex justify-between items-center gap-3" data-id="element-95">
        {isEditing ? <>
            <button onClick={handleCancel} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-96">
              Cancel
            </button>
            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-emerald-500/20" data-id="element-97">
              <Check className="w-4 h-4" data-id="element-98" />
              Save Changes
            </button>
          </> : <>
            <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-99">
              <Settings2 className="w-4 h-4" data-id="element-100" />
              Edit Criteria
            </button>
            <button onClick={handleSimulate} className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-indigo-500/20" data-id="element-101">
              Simulate Changes
              <ArrowRight className="w-4 h-4" data-id="element-102" />
            </button>
          </>}
      </div>
    </div>;
}
// ---------- Group ----------
interface RuleGroupProps {
  label: string;
  tone: 'indigo' | 'amber' | 'rose';
  rules: Rule[];
  isEditing: boolean;
  connected?: boolean;
  isExclusion?: boolean;
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<Rule>) => void;
  onDelete: (id: string) => void;
}
const TONE_STYLES: Record<RuleGroupProps['tone'], string> = {
  indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
};
function RuleGroup({
  label,
  tone,
  rules,
  isEditing,
  connected,
  isExclusion,
  onAdd,
  onUpdate,
  onDelete
}: RuleGroupProps) {
  return <section data-id="element-103">
      <div className="flex items-center gap-2 mb-4" data-id="element-104">
        <div className={`px-2 py-1 text-xs font-bold rounded border ${TONE_STYLES[tone]}`} data-id="element-105">
          {label}
        </div>
        <div className="h-px flex-1 bg-slate-800" data-id="element-106"></div>
      </div>

      <div className={`space-y-3 ${connected ? 'relative pl-6' : ''}`} data-id="element-107">
        {connected && rules.length > 0 && <div className="absolute left-2 top-3 bottom-3 w-px bg-slate-800" aria-hidden="true" data-id="element-108"></div>}

        {rules.length === 0 && <div className="text-xs text-slate-500 italic py-2 px-3 border border-dashed border-slate-800 rounded-lg" data-id="element-109">
            No rules in this group.
          </div>}

        {rules.map(rule => <div key={rule.id} className="relative" data-id="element-110">
            {connected && <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-4 h-px bg-slate-800" aria-hidden="true" data-id="element-111"></div>}
            <RuleRow rule={rule} isEditing={isEditing} isExclusion={isExclusion} onUpdate={patch => onUpdate(rule.id, patch)} onDelete={() => onDelete(rule.id)} data-id="element-112" />
          </div>)}

        {isEditing && <button onClick={onAdd} className="w-full flex items-center justify-center gap-2 py-2 px-3 text-xs font-medium text-slate-400 hover:text-white border border-dashed border-slate-800 hover:border-slate-700 hover:bg-slate-800/30 rounded-lg transition-colors" data-id="element-113">
            <Plus className="w-3.5 h-3.5" data-id="element-114" />
            Add Rule
          </button>}
      </div>
    </section>;
}
// ---------- Row ----------
interface RuleRowProps {
  rule: Rule;
  isEditing: boolean;
  isExclusion?: boolean;
  onUpdate: (patch: Partial<Rule>) => void;
  onDelete: () => void;
}
function RuleRow({
  rule,
  isEditing,
  isExclusion,
  onUpdate,
  onDelete
}: RuleRowProps) {
  if (isEditing) {
    return <div className={`border rounded-lg p-3 transition-colors ${isExclusion ? 'bg-rose-950/20 border-rose-900/50' : 'bg-slate-950 border-slate-800'}`} data-id="element-115">
        <div className="flex items-center gap-2" data-id="element-116">
          <input type="text" value={rule.label} onChange={e => onUpdate({
          label: e.target.value
        })} list={`fields-${rule.id}`} placeholder="Field" className="flex-1 min-w-0 bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50" data-id="element-117" />
          <datalist id={`fields-${rule.id}`} data-id="element-118">
            {FIELD_SUGGESTIONS.map(f => <option key={f} value={f} data-id="element-119" />)}
          </datalist>

          <select value={rule.op} onChange={e => onUpdate({
          op: e.target.value
        })} className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-xs font-mono text-slate-200 focus:outline-none focus:border-indigo-500/50 cursor-pointer" data-id="element-120">
            {OPERATORS.map(op => <option key={op} value={op} data-id="element-121">
                {op}
              </option>)}
          </select>

          <input type="text" value={rule.value} onChange={e => onUpdate({
          value: e.target.value
        })} placeholder="Value" className="w-24 bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-slate-200 tabular-nums placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50" data-id="element-122" />

          <button onClick={onDelete} aria-label="Delete rule" className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded transition-colors shrink-0" data-id="element-123">
            <Trash2 className="w-3.5 h-3.5" data-id="element-124" />
          </button>
        </div>
      </div>;
  }
  if (isExclusion) {
    return <div className="bg-rose-950/20 border border-rose-900/50 rounded-lg p-3 flex items-center justify-between" data-id="element-125">
        <div className="flex items-center gap-3" data-id="element-126">
          <div className="w-8 h-8 rounded bg-rose-950/50 flex items-center justify-center border border-rose-900/50" data-id="element-127">
            <ShieldAlert className="w-4 h-4 text-rose-400" data-id="element-128" />
          </div>
          <div data-id="element-129">
            <div className="text-sm font-medium text-rose-200" data-id="element-130">
              {rule.label}
            </div>
            {rule.sub && <div className="text-xs text-rose-400/70" data-id="element-131">{rule.sub}</div>}
          </div>
        </div>
        <span className="px-2.5 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded text-sm font-medium" data-id="element-132">
          {rule.value || 'Active'}
        </span>
      </div>;
  }
  return <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 flex items-center justify-between hover:border-slate-700 transition-colors" data-id="element-133">
      <div className="flex items-center gap-3 min-w-0" data-id="element-134">
        {rule.sub !== undefined && <div className="w-8 h-8 rounded bg-slate-900 flex items-center justify-center border border-slate-800 shrink-0" data-id="element-135">
            <Filter className="w-4 h-4 text-slate-400" data-id="element-136" />
          </div>}
        <div className="min-w-0" data-id="element-137">
          <div className="text-sm font-medium text-slate-200 truncate" data-id="element-138">
            {rule.label}
          </div>
          {rule.sub && <div className="text-xs text-slate-500 truncate" data-id="element-139">{rule.sub}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0" data-id="element-140">
        <span className="text-xs text-slate-400 font-mono" data-id="element-141">{rule.op}</span>
        <span className={`px-2.5 py-1 rounded text-sm font-medium tabular-nums border ${rule.tone === 'success' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-slate-800 text-white border-slate-700'}`} data-id="element-142">
          {rule.value}
        </span>
      </div>
    </div>;
}